import type { Timers } from './timers.ts';
import type { DawaSuggestion, FetchImpl, SuggestionType } from './types.ts';
import { warn } from './warn.ts';

export interface ControllerOptions {
	baseUrl: string;
	apiKey: string | undefined;
	type: SuggestionType;
	fuzzy: boolean;
	params: Record<string, string | number | boolean>;
	stormodtagerpostnumre: boolean;
	supplerendebynavn: boolean;
	minLength: number;
	retryDelay: number;
	fetchImpl: FetchImpl;
	/** The input's window's timers (retry back-off). */
	timers: Timers;
}

export interface ControllerCallbacks {
	/** New suggestions to show (an empty list closes the list). */
	render(suggestions: DawaSuggestion[]): void;
	/** A complete address was picked. */
	select(item: DawaSuggestion): void;
	/** The address named by `id` was found: put its text in the field. */
	initial(item: DawaSuggestion): void;
}

type Request =
	| { kind: 'text'; text: string; caretpos: number }
	| { kind: 'pick'; item: DawaSuggestion }
	| { kind: 'id'; id: string };

/** A request that failed on a status a retry cannot fix (bad key, wrong origin, bad params). */
class FatalResponse extends Error {}

/** Retries per request before it is given up (network errors, 5xx, 429). */
const MAX_RETRIES = 3;

/**
 * Talks to `/autocomplete` and decides what a pick means: picking a street or an access address
 * asks for the next stage, picking a suggestion of the final type is the selection.
 *
 * One request runs at a time. While it runs, only the newest waiting request is kept, so a fast
 * typist never sees an older answer arrive after a newer one.
 */
export class AutocompleteController {
	private current: Request | null = null;
	private pending: Request | null = null;
	private destroyed = false;
	private retries = 0;
	selected: DawaSuggestion | null = null;

	constructor(
		private readonly o: ControllerOptions,
		private readonly cb: ControllerCallbacks,
	) {}

	update(text: string, caretpos: number): void {
		this.schedule({ kind: 'text', text, caretpos });
	}

	pick(item: DawaSuggestion): void {
		this.schedule({ kind: 'pick', item });
	}

	selectInitial(id: string): void {
		this.schedule({ kind: 'id', id });
	}

	destroy(): void {
		this.destroyed = true;
		this.current = null;
		this.pending = null;
	}

	private schedule(request: Request): void {
		if (this.destroyed) return;
		if (this.current) {
			this.pending = request;
			return;
		}
		this.current = request;
		this.retries = 0;
		void this.execute(request);
	}

	private next(): void {
		this.current = this.pending;
		this.pending = null;
		this.retries = 0;
		if (this.current && !this.destroyed) void this.execute(this.current);
	}

	private async execute(request: Request): Promise<void> {
		let result: DawaSuggestion[];
		try {
			if (request.kind === 'id') {
				result = await this.fetch({ id: request.id, type: this.o.type });
			} else if (request.kind === 'pick') {
				const item = request.item;
				if (item.type === this.o.type) {
					this.selected = item;
					this.cb.select(item);
					this.next();
					return;
				}
				result = await this.fetch(this.searchParams(item.tekst, item.caretpos, item));
			} else if (request.text.length >= this.o.minLength) {
				result = await this.fetch(this.searchParams(request.text, request.caretpos, null));
			} else {
				result = [];
			}
		} catch (error: unknown) {
			if (this.destroyed) return;
			if (error instanceof FatalResponse || this.retries >= MAX_RETRIES) {
				if (!(error instanceof FatalResponse)) warn(`lookup failed: ${String(error)}`);
				this.next();
				return;
			}
			this.retries += 1;
			this.o.timers.setTimeout(() => {
				if (this.destroyed) return;
				// A newer request supersedes the retry.
				if (this.pending) this.next();
				else void this.execute(request);
			}, this.o.retryDelay);
			return;
		}
		if (this.destroyed) return;
		this.handle(request, result);
		this.next();
	}

	private handle(request: Request, result: DawaSuggestion[]): void {
		if (request.kind === 'id') {
			const hit = result.length === 1 ? result[0] : undefined;
			if (hit) {
				this.selected = hit;
				this.cb.initial(hit);
			}
			return;
		}
		if (request.kind === 'pick' && result.length === 1) {
			const only = result[0]!;
			if (only.type === this.o.type) {
				// The pick narrowed to exactly one complete address: that is the selection.
				this.selected = only;
				this.cb.select(only);
			} else if (!this.pending) {
				// One street or one access address: step straight on to the next stage.
				this.pending = { kind: 'pick', item: only };
			}
			return;
		}
		this.cb.render(result);
	}

	private searchParams(
		text: string,
		caretpos: number,
		picked: DawaSuggestion | null,
	): Record<string, string> {
		const p: Record<string, string> = {};
		for (const [k, v] of Object.entries(this.o.params)) p[k] = String(v);
		p.q = text;
		p.type = this.o.type;
		p.caretpos = String(caretpos);
		p.supplerendebynavn = String(this.o.supplerendebynavn);
		p.stormodtagerpostnumre = String(this.o.stormodtagerpostnumre);
		p.multilinje = 'true';
		if (this.o.fuzzy) p.fuzzy = '';
		if (picked?.type === 'adgangsadresse' && typeof picked.data.id === 'string') {
			p.adgangsadresseid = picked.data.id;
		}
		if (picked?.type === 'vejnavn') p.startfra = 'adgangsadresse';
		return p;
	}

	private async fetch(params: Record<string, string>): Promise<DawaSuggestion[]> {
		const qs = Object.keys(params).map((k) => `${k}=${encodeURIComponent(params[k] ?? '')}`);
		// The key rides as ?token= on a plain GET, so the browser sends no CORS preflight.
		if (this.o.apiKey) qs.push(`token=${encodeURIComponent(this.o.apiKey)}`);
		const res = await this.o.fetchImpl(`${this.o.baseUrl}/autocomplete?${qs.join('&')}`);
		if (!res.ok) {
			const message = await res
				.json()
				.then((b: unknown) =>
					b && typeof b === 'object' && 'error' in b ? String(b.error) : '',
				)
				.catch(() => '');
			const line = `${res.status} from ${this.o.baseUrl}/autocomplete${message ? `: ${message}` : ''}`;
			// 429 and 5xx can pass; anything else (no key, wrong origin, bad parameter) will not.
			if (res.status === 429 || res.status >= 500) throw new Error(line);
			warn(line);
			throw new FatalResponse(line);
		}
		const body: unknown = await res.json();
		return Array.isArray(body) ? (body as DawaSuggestion[]) : [];
	}
}
